import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type {
	ILocalizedInformation,
	IShoppingProduct,
	ITheme,
	ITotalPriceSummary
} from '@inter-ikea-kompis/types';
import {
	DexfSettingsUtility,
	PriceUtility,
	TotalPriceCalculator
} from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { EcoFee } from '@inter-ikea-kompis/component-eco-fee';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { Text } from '@inter-ikea-kompis/component-text';

import type { CommercialMessageVariant } from '@ingka/commercial-message-webc';
import { CommercialMessage as SkapaCommercialMessage } from '@ingka/commercial-message-webc/passive.js';

import MiniPriceAlignmentEnum from '../../enums/MiniPriceAlignmentEnum.js';
import MiniPriceModalEnum from '../../enums/MiniPriceModalEnum.js';
import type IMiniPriceDexfSettings from '../../types/IMiniPriceDexfSettings.js';
import type IMiniPriceTranslations from '../../types/IMiniPriceTranslations.js';
import MiniPriceStyle from './MiniPriceStyle.js';

/**
 * This price layout is typically used for showing the total price in the planning step through the Mini Configuration Summary and the Planning Header. It has reduced information, e.g. it does not support all commercial messages or compliance requirements.
 *
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.modalType tells you the type
 *
 * @slot Text displayed after the fraction part.
 */
export default class MiniPrice extends KompisElement {
	// Accepts a object format corresponding to { ShoppingProduct } from '@inter-ikea-kompis/types'
	@property({ type: Object })
	public shoppingProducts!: IShoppingProduct[];

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IMiniPriceTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IMiniPriceDexfSettings | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Set price to be animated
	@property({ type: Boolean })
	public animatedPrice = false;

	// Align slot
	@property({ type: String })
	public align: MiniPriceAlignmentEnum | null = null;

	// Show eco fee
	@property({ type: Boolean })
	public showEcoFee = false;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Show a modal.
	@property({ type: String })
	public visibleModal: MiniPriceModalEnum | null = null;

	private dexfSettings: IMiniPriceDexfSettings | null = null;

	/**
	 * Renders view.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		if (!this.shoppingProducts) {
			return null;
		}

		const totalPrice = TotalPriceCalculator.calculateMultiple(
			this.shoppingProducts,
			this.getSettings().localisation.dateFormat
		);

		const primaryCurrency = totalPrice[0];

		return html`
			<style>
				${MiniPriceStyle.getStyle({
					theme: this.theme,
					align: this.align,
					hasDualCurrencies: totalPrice.length > 1,
					isTRO: PriceUtility.isTro(primaryCurrency)
				})}
			</style>

			${this.getCommercialMessage(primaryCurrency)}

			<div class="priceContainer">
				${this.align === MiniPriceAlignmentEnum.right
					? html`
							<slot></slot>
							<div class="divider"></div>
						`
					: ''}

				<div class="innerPriceContainer">
					${totalPrice.map((price, index) => {
						const isPrimaryCurrency = index < 1;
						return html`
							${this.getRegularPrice(price)} ${this.getPrice(price, isPrimaryCurrency)}
						`;
					})}
				</div>

				${this.align !== MiniPriceAlignmentEnum.right
					? html`
							<slot></slot>
							<div class="divider"></div>
						`
					: ''}
			</div>

			${this.getText(primaryCurrency)} ${this.getEcoFee(primaryCurrency)}
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-commercial-message', SkapaCommercialMessage);
		this.customElements.define('kompis-price-formatter', PriceFormatter);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-eco-fee', EcoFee);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): IMiniPriceTranslations {
		return this.translations
			? this.translations
			: <IMiniPriceTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): IMiniPriceDexfSettings {
		return <IMiniPriceDexfSettings>this.dexfSettings;
	}

	/**
	 * Returns eco fee.
	 *
	 * @param totalPrice Price model.
	 * @returns Template.
	 */
	private getEcoFee(totalPrice: ITotalPriceSummary): RenderResult {
		if (!this.showEcoFee || (!totalPrice.ecoPrice && !totalPrice.weeePrice)) {
			return null;
		}

		return html`
			<kompis-eco-fee
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.theme="${this.theme}"
				.totalPrice="${totalPrice}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal ? this.visibleModal : null}"
				@modalClose="${(event: CustomEvent) => {
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
					);
					event.detail?.triggerElement?.focus();
				}}"
				@modalOpen="${() =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', {
							bubbles: true,
							detail: { visibleModal: MiniPriceModalEnum.ecoFeeCard }
						})
					)}"
			></kompis-eco-fee>
		`;
	}

	/**
	 * Get the commercial message.
	 *
	 * @param totalPrice Price model.
	 * @returns Commercial message.
	 */
	private getCommercialMessage(totalPrice: ITotalPriceSummary): RenderResult {
		let variant: CommercialMessageVariant | undefined;
		if (PriceUtility.isFamily(totalPrice)) {
			variant = 'family';
		}

		if (!variant) {
			return null;
		}

		return html`
			<skapa-commercial-message variant="${variant}" subtle>
				${this.getTranslations().priceInfoIkeaFamilyPrice}
			</skapa-commercial-message>
		`;
	}

	/**
	 * Get the regular price.
	 *
	 * @param totalPrice Price model.
	 * @returns Regular price.
	 */
	private getRegularPrice(totalPrice: ITotalPriceSummary): RenderResult {
		if (!PriceUtility.isTro(totalPrice)) {
			return null;
		}

		return html`
			<kompis-text
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.priceXSmall}"
				.bold="${true}"
			>
				<kompis-price-formatter
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.amount="${totalPrice.regularInclTax}"
					.strikeThrough="${true}"
					.ariaLabelPrice="${this.getTranslations().priceInfoRegularPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>
			</kompis-text>
		`;
	}

	/**
	 * Get the current price.
	 *
	 * @param totalPrice Price model.
	 * @param isPrimaryCurrency ...
	 * @returns Current price.
	 */
	private getPrice(totalPrice: ITotalPriceSummary, isPrimaryCurrency: boolean): RenderResult {
		return html`
			<kompis-text
				class="price"
				.theme="${this.theme}"
				.type="${this.getPriceFontStyleType(totalPrice, isPrimaryCurrency)}"
				.bold="${true}"
			>
				<kompis-price-formatter
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.animated="${this.animatedPrice}"
					.amount="${PriceUtility.getPrice(totalPrice)}"
					.mixedSize="${this.getPriceMixedSize(totalPrice, isPrimaryCurrency)}"
					.ariaLabelPrice="${this.getTranslations().priceInfoCurrentPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>
			</kompis-text>
		`;
	}

	/**
	 * Get the text.
	 *
	 * @param totalPrice Price model.
	 * @returns The text.
	 */
	private getText(totalPrice: ITotalPriceSummary): RenderResult {
		if (!PriceUtility.isTroOrFamily(totalPrice)) {
			return null;
		}

		if (PriceUtility.isFamily(totalPrice)) {
			return html`${this.getRegularPriceText(totalPrice)} ${this.getValidTimePeriod(totalPrice)}`;
		}

		if (PriceUtility.isTro(totalPrice)) {
			return html`${this.getValidTimePeriod(totalPrice)}`;
		}

		return null;
	}

	/**
	 * Get correct translation for time period.
	 *
	 * @param totalPrice Price model.
	 * @returns Translated price period.
	 */
	private getValidTimePeriod(totalPrice: ITotalPriceSummary): RenderResult {
		const settings = this.getSettings();

		const updatedSettings: IMiniPriceDexfSettings = {
			...settings,
			kompis: {
				...settings.kompis,
				priceSettings: {
					...settings.kompis.priceSettings,
					temporaryPricePeriod: settings.kompis.priceSettings.temporaryPricePeriodMiniSummary
				}
			}
		};

		const validInterval = PriceUtility.getValidInterval(
			totalPrice,
			undefined,
			this.getTranslations(),
			updatedSettings
		);

		return validInterval
			? html`
					<kompis-text
						class="text"
						.theme="${this.theme}"
						.type="${ThemeFontStyleTypeEnum.bodySmall}"
					>
						${validInterval}
					</kompis-text>
				`
			: null;
	}

	/**
	 * Get regular price text.
	 * If loyaltyProgramEqualFontSize is true, it will render the same amount, but with enlarged font size.
	 *
	 * @param totalPrice Price model.
	 * @returns Regular price text.
	 */
	private getRegularPriceText(totalPrice: ITotalPriceSummary): RenderResult {
		const loyaltyProgramEqualFontSize =
			this.getSettings().kompis.priceSettings.loyaltyProgramEqualFontSize;
		const priceInfoRegularPrice = this.getTranslations().priceInfoRegularPrice;

		if (loyaltyProgramEqualFontSize) {
			return html`
				<kompis-text
					aria-hidden="true"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
				>
					${priceInfoRegularPrice}
				</kompis-text>

				<kompis-text
					class="price"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${this.getPriceFontStyleType(totalPrice, true)}"
				>
					<kompis-price-formatter
						.localizedInformation="${this.localizedInformation}"
						.settings="${this.settings}"
						.animated="${this.animatedPrice}"
						.amount="${totalPrice.regularInclTax}"
						.mixedSize="${this.getPriceMixedSize(totalPrice, true)}"
						.ariaLabelPrice="${priceInfoRegularPrice}"
						.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
					>
					</kompis-price-formatter>
				</kompis-text>
			`;
		}

		return html`
			<kompis-text class="text" .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				<div class="regularPrice">
					<span aria-hidden="true">${priceInfoRegularPrice}:</span>
					<div class="regularPriceTextDivider"></div>

					<kompis-price-formatter
						.localizedInformation="${this.localizedInformation}"
						.settings="${this.settings}"
						.amount="${totalPrice.regularInclTax}"
						.ariaLabelPrice="${priceInfoRegularPrice}"
					>
					</kompis-price-formatter>
				</div>
			</kompis-text>
		`;
	}

	/**
	 * Price font style type.
	 *
	 * @param totalPrice Price model.
	 * @param isPrimaryCurrency ...
	 * @returns Font style type.
	 */
	private getPriceFontStyleType(
		totalPrice: ITotalPriceSummary,
		isPrimaryCurrency: boolean
	): ThemeFontStyleTypeEnum {
		if (PriceUtility.isFamily(totalPrice)) {
			return isPrimaryCurrency
				? ThemeFontStyleTypeEnum.priceMedium
				: ThemeFontStyleTypeEnum.priceXSmall;
		}

		if (PriceUtility.isTro(totalPrice)) {
			return ThemeFontStyleTypeEnum.priceMedium;
		}

		return isPrimaryCurrency
			? ThemeFontStyleTypeEnum.priceXLarge
			: ThemeFontStyleTypeEnum.priceMedium;
	}

	/**
	 * Returns if price is mixed size.
	 *
	 * @param totalPrice ...
	 * @param isPrimaryCurrency ...
	 * @returns Mixed size.
	 */
	private getPriceMixedSize(totalPrice: ITotalPriceSummary, isPrimaryCurrency: boolean): boolean {
		if (PriceUtility.isFamily(totalPrice) && !isPrimaryCurrency) {
			return false;
		}

		return true;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-commercial-message': SkapaCommercialMessage;
		'kompis-mini-price': MiniPrice;
	}
}
